import React from 'react';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { TextInput } from 'playup-web-core';
import TagManager from 'react-gtm-module';

import LoadingButton from '../../../components/LoadingButton';
import useNotify from '../../../hooks/useNotify';
import appConfig from '../../../appConfig';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { createExternalDeposit } from '../../../api/deposit';
import { parseApiError } from '../../../utils/parseApiError';

interface Props {
	amount: number;
	selectedPromotion?: {
		id: string;
		client_id: number;
	};
	setAmountError: (amountError: string | undefined) => void;
}

interface FormData {
	date: string;
}

const Eft: React.FC<Props> = ({ amount, selectedPromotion, setAmountError }) => {
	const [sending, setSending] = React.useState<boolean>(false);
	const [sentSuccessful, setSentSuccessful] = React.useState<boolean>(false);
	const [errors, setErrors] = React.useState<APIError>();
	const { register, handleSubmit } = useForm<FormData>({
		defaultValues: {
			date: dayjs().format('YYYY-MM-DD'),
		},
	});

	const { user } = React.useContext(PortalSessionContext);
	const notify = useNotify();

	const onSubmit = (data: FormData) => {
		setSending(true);
		setSentSuccessful(false);
		setErrors(undefined);

		createExternalDeposit({
			date: dayjs(data.date).format('YYYY-MM-DD'),
			amount,
			deposit_request_type: 'EFT',
			promotion: selectedPromotion,
		})
			.then((response) => {
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your Deposit Notice has been received.',
					timeout: 5000,
				});
				if (appConfig.googleTagManagerID) {
					TagManager.dataLayer({
						dataLayer: {
							event: 'deposit',
							deposit_amount: amount,
							deposit_method: 'EFT',
						},
					});
				}
				setSentSuccessful(true);
			})
			.catch((error) => {
				setErrors(error.errors);
				setAmountError(parseApiError(error.errors, 'amount'));
			})
			.finally(() => {
				setSending(false);
			});
	};

	return (
		<div className="_px-4 _text-black">
			<p className="_text-sm">
				Use this feature to advise us that you have already deposited money into our bank account.
			</p>
			<p className="_pt-2 _font-bold">Bank Details</p>
			<div className="_flex _flex-col _py-2 _text-xs md:_text-base">
				<div className="_flex">
					<span className="_w-32 _pb-1 _mr-4 _font-semibold _text-right">Bank Name</span>
					{appConfig.brand.eft.bank_name}
				</div>
				<div className="_flex">
					<span className="_w-32 _pb-1 _mr-4 _font-semibold _text-right">Account Name</span>
					{appConfig.brand.eft.account_name}
				</div>
				<div className="_flex">
					<span className="_w-32 _pb-1 _mr-4 _font-semibold _text-right">Account Number</span>
					{appConfig.brand.eft.account_number}
				</div>
				<div className="_flex">
					<span className="_w-32 _pb-1 _mr-4 _font-semibold _text-right">BSB Number</span>
					{appConfig.brand.eft.bsb_number}
				</div>
			</div>
			<div className="_px-4 _py-2 _mt-2 _text-blue-600 _bg-blue-100 _rounded">
				<p className="_font-bold">IMPORTANT!</p>
				<p className="_pt-2">
					Please use your UserID <span className="_font-extrabold">({user?.name || ''})</span> as the reference number
					on your deposit slip, or in the description box for internet banking.
				</p>
			</div>
			<div className="_mt-4 _border-t _border-gray-200" />
			<div>
				<form onSubmit={handleSubmit(onSubmit)} className="_px-2 _pb-4">
					<TextInput
						kind="white"
						type="date"
						label="Deposit Date"
						error={parseApiError(errors, 'date')}
						className="_mt-4"
						{...register('date', { required: true })}
					/>
					<div className="_flex _items-center _justify-end md:_px-8">
						<LoadingButton type="submit" loading={sending} className="_w-full _mt-4 _btn _btn-primary _rounded">
							Deposit
						</LoadingButton>
					</div>
					{errors && errors.error && <p className="_text-center _text-red-700">{parseApiError(errors)}</p>}
					{sentSuccessful && (
						<p className="_text-center _text-green-700 _mt-2">Your Deposit Notice has been received</p>
					)}
				</form>
			</div>
		</div>
	);
};

export default Eft;
